import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  Mail,
  Building2,
  Globe,
  Calendar,
  Layers,
  User,
  MessageSquare,
  RefreshCw,
  Inbox,
  CheckCircle2,
  Handshake,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new: { label: "New", color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", icon: Inbox },
  replied: { label: "Replied", color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: CheckCircle2 },
  booked: { label: "Booked", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Handshake },
};

const PRODUCT_META = {
  accelerator: { label: "Accelerator", pill: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  campus:      { label: "Campus",      pill: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  panels:      { label: "Panels",      pill: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  sprint:      { label: "Sprint",      pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};

const PRODUCT_TABS = [
  { key: "all",         label: "All products" },
  { key: "accelerator", label: "Accelerator" },
  { key: "campus",      label: "Campus" },
  { key: "panels",      label: "Panels" },
  { key: "sprint",      label: "Sprint" },
];

const TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "replied", label: "Replied" },
  { key: "booked", label: "Booked" },
];

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminProgrammeEnquiries() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, replied: 0, booked: 0 });
  const [activeTab, setActiveTab] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (!res.ok) {
          navigate("/auth");
          return;
        }
        const data = await res.json();
        setUser(data);
      } catch {
        navigate("/auth");
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (user) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, productFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = productFilter && productFilter !== "all" ? `?product=${productFilter}` : "";
      const res = await fetch(`${BACKEND_URL}/api/admin/programme-enquiries${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, new: 0, replied: 0, booked: 0 });
    } catch {
      toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/programme-enquiries/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Marked as ${STATUS_META[status].label.toLowerCase()}.`);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      // recompute counts
      setCounts((prev) => {
        const next = { ...prev };
        const item = items.find((i) => i.id === id);
        if (item) {
          const oldStatus = item.status || "new";
          if (oldStatus !== status) {
            next[oldStatus] = Math.max(0, (next[oldStatus] || 0) - 1);
            next[status] = (next[status] || 0) + 1;
          }
        }
        return next;
      });
    } catch {
      toast.error("Failed to update status.");
    } finally {
      setUpdating(false);
    }
  };

  const filtered = activeTab === "all" ? items : items.filter((i) => (i.status || "new") === activeTab);

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin")}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="btn-back-admin"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="text-[10px] font-mono text-cyan-400 tracking-[0.3em] uppercase mb-1">
              Product enquiries
            </div>
            <h1 className="text-3xl font-bold text-white">Enquiries Inbox</h1>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
            data-testid="btn-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { key: "total", label: "Total", value: counts.total, tone: "text-white" },
            { key: "new", label: "New", value: counts.new, tone: "text-cyan-300" },
            { key: "replied", label: "Replied", value: counts.replied, tone: "text-amber-300" },
            { key: "booked", label: "Booked", value: counts.booked, tone: "text-emerald-300" },
          ].map((s) => (
            <Card key={s.key} className="bg-slate-900 border-slate-800" data-testid={`stat-${s.key}`}>
              <CardContent className="p-4">
                <div className="text-[10px] font-mono text-slate-500 tracking-[0.25em] uppercase mb-1">
                  {s.label}
                </div>
                <div className={`text-3xl font-extrabold ${s.tone}`}>{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Product filter */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="product-filter">
          {PRODUCT_TABS.map((p) => {
            const active = productFilter === p.key;
            const n = p.key === "all" ? counts.total : (counts.by_product?.[p.key] ?? 0);
            return (
              <button
                key={p.key}
                onClick={() => setProductFilter(p.key)}
                data-testid={`product-filter-${p.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200"
                    : "border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-700 hover:text-slate-200"
                }`}
              >
                {p.label} <span className="opacity-60 ml-1">({n})</span>
              </button>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-slate-800">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? "border-cyan-500 text-white"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              {t.label}
              <span className="ml-2 text-xs text-slate-500">
                ({t.key === "all" ? counts.total : counts[t.key] || 0})
              </span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-800 rounded-2xl">
            <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <div className="text-slate-400">No enquiries in this tab yet.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((e) => (
              <button
                key={e.id}
                onClick={() => setSelected(e)}
                data-testid={`enquiry-row-${e.id}`}
                className="w-full text-left rounded-xl border border-slate-800 bg-slate-900 hover:border-cyan-500/40 hover:bg-slate-900/80 transition-colors p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-semibold text-white truncate">{e.name}</span>
                      <StatusPill status={e.status || "new"} />
                      {e.product && PRODUCT_META[e.product] && (
                        <span className={`inline-flex items-center text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-full border ${PRODUCT_META[e.product].pill}`}>
                          {PRODUCT_META[e.product].label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{e.company}</span>
                      <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{e.country}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{e.email}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(e.submitted_at)}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {e.format && (
                        <span className="text-[11px] font-mono text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded">
                          {e.format}
                        </span>
                      )}
                      {e.timing && (
                        <span className="text-[11px] font-mono text-slate-400 bg-slate-800/60 border border-slate-700 px-2 py-0.5 rounded">
                          {e.timing}
                        </span>
                      )}
                      {!e.format && !e.timing && e.message && (
                        <span className="text-[11px] text-slate-400 italic line-clamp-1 max-w-full">
                          “{e.message}”
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 mt-2 shrink-0" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="enquiry-detail-modal"
        >
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-[10px] font-mono text-cyan-400 tracking-[0.3em] uppercase">
                    Enquiry
                  </div>
                  <StatusPill status={selected.status || "new"} />
                </div>
                <DialogTitle className="text-2xl font-bold text-white">
                  {selected.name}
                </DialogTitle>
                <div className="text-sm text-slate-400 mt-1">
                  {formatDate(selected.submitted_at)}
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-2 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field icon={Mail} label="Email">
                    <a href={`mailto:${selected.email}`} className="text-cyan-300 hover:underline">
                      {selected.email}
                    </a>
                  </Field>
                  <Field icon={Building2} label="Company">
                    {selected.company}
                  </Field>
                  <Field icon={Globe} label="Country">
                    {selected.country}
                  </Field>
                  <Field icon={User} label="Role">
                    {selected.role || "—"}
                  </Field>
                  <Field icon={Layers} label="Format">
                    {selected.format}
                  </Field>
                  <Field icon={Calendar} label="Timing">
                    {selected.timing}
                  </Field>
                </div>

                <div>
                  <div className="text-[10px] font-mono text-slate-500 tracking-[0.25em] uppercase mb-2 flex items-center gap-1.5">
                    <MessageSquare className="w-3 h-3" />
                    Description / comment
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 whitespace-pre-wrap text-slate-200 leading-relaxed">
                    {selected.description || <span className="text-slate-500 italic">No additional notes provided.</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-800">
                  <div className="text-[10px] font-mono text-slate-500 tracking-[0.25em] uppercase mb-3">
                    Update status
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(STATUS_META).map((key) => {
                      const meta = STATUS_META[key];
                      const Icon = meta.icon;
                      const active = (selected.status || "new") === key;
                      return (
                        <Button
                          key={key}
                          disabled={updating || active}
                          onClick={() => updateStatus(selected.id, key)}
                          data-testid={`btn-status-${key}`}
                          variant={active ? "default" : "outline"}
                          className={
                            active
                              ? "bg-cyan-500 hover:bg-cyan-500 text-slate-950 cursor-default"
                              : "border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800"
                          }
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {active ? `Currently ${meta.label}` : `Mark ${meta.label}`}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="mt-4">
                    <Button
                      asChild
                      variant="outline"
                      className="border-cyan-500/40 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
                      data-testid="btn-reply-email"
                    >
                      <a href={`mailto:${selected.email}?subject=Re:%20Your%20Operon%20Programmes%20enquiry`}>
                        <Mail className="w-4 h-4 mr-2" />
                        Reply by email
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono text-slate-500 tracking-[0.25em] uppercase mb-1 flex items-center gap-1.5">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="text-slate-100">{children}</div>
    </div>
  );
}
